<template>
	<div :class="classes">
		<div v-if="withKeyword" class="characteristic-select-inline--search">
			<SearchInput
				v-model="keyword"
				:placeholder="keywordPlaceholder"
				:characteristic-type="characteristicType"
				:catalog="searchCatalog"
			/>
		</div>
		<div v-if="isNothingAllowed" class="characteristic-select-inline--nothing-allowed">
			<b-form-checkbox
				:checked="isNothingChecked"
				@change="nothingChanged"
			>
				{{ nothingAllowed.text }}
			</b-form-checkbox>
			<div v-if="!suppressItemsInfo" class="characteristic-select-inline--items-info">
				<template v-if="showMultipleHint">
					Oder <span class="font-weight-extra-bold">Mehrfachauswahl</span> möglich:
				</template>
				<template v-else>Oder wähle aus:</template>
			</div>
		</div>
		<div
			v-else-if="showMultipleHint && filteredCharacteristicItems.length > 0"
			class="characteristic-select-inline--items-info font-weight-extra-bold"
		>
			Mehrfachauswahl möglich:
		</div>
		<div v-if="filteredCharacteristicItems.length === 0">
			Kein Treffer gefunden. Bitte gib ein anderes Wort ein, das dasselbe oder etwas Ähnliches bedeutet.
		</div>
		<div
			v-for="itemGroup in itemsGroupedByLetter"
			:key="itemGroup.id"
		>
			<Separation
				v-if="itemGroup.separation"
				:separation="itemGroup.separation"
				class="characteristic-select-inline--items-separation"
			/>
			<div
				v-for="(characteristics, letter) in itemGroup.items"
				:key="letter"
				class="characteristic-select-inline--letter-section"
			>
				<div class="characteristic-select-inline--letter-section-letter">{{ letter }}</div>
				<div class="characteristic-select-inline--letter-section-items">
					<div
						v-for="characteristic in characteristics"
						:key="characteristic.id"
						:class="['characteristic-select-inline--item', { 'has-icon': hasIcon(characteristic) } ]"
					>
						<div class="characteristic-select-inline--item-label">
							<b-form-radio
								v-if="singleChoice"
								:checked="isSelected(characteristic) ? characteristic.id : undefined"
								:name="'radio-' + key"
								:value="characteristic.id"
								:ref="'characteristic-select-' + characteristic.id"
								:class="{'has-childs': hasChilds(characteristic)}"
								:disabled="disabled"
								@change="selectOrUnselect(characteristic, true)"
							>
								<ValueLabel
									:characteristic="characteristic"
									:has-icon="hasIcon(characteristic)"
									:icon-path="iconPath"
									:description-config="descriptionConfig"
									:label-type="labelType"
									:highlight-words="highlightWords"
								/>
							</b-form-radio>
							<b-form-checkbox
								v-else
								:checked="isSelected(characteristic)"
								:class="{'has-childs': hasChilds(characteristic)}"
								:ref="'characteristic-select-' + characteristic.id"
								:disabled="disabled"
								@change="selectOrUnselect(characteristic, $event)"
							>
								<ValueLabel
									:characteristic="characteristic"
									:has-icon="hasIcon(characteristic)"
									:icon-path="iconPath"
									:description-config="descriptionConfig"
									:label-type="labelType"
									:highlight-words="highlightWords"
								/>
							</b-form-checkbox>
							<a
								v-if="isSelected(characteristic) && hasChilds(characteristic)"
								href="#"
								@click.prevent="openFan(characteristic)"
							>
								<Icon icon="pencil"/>
							</a>
						</div>
						<div v-if="mustChooseSupplements(characteristic)">
							<Supplements
								v-for="itemCharacteristicType in relevantItemCharacteristicTypes(characteristic)"
								:key="itemCharacteristicType.id"
								:item-characteristic-type="itemCharacteristicType"
								:initial-selected="getInitialSupplements(characteristic, itemCharacteristicType)"
								:nothing-allowed="supplementsConfig && supplementsConfig.nothingAllowed !== undefined ? supplementsConfig.nothingAllowed : true"
								:label-type="labelType"
								@input="setSupplements(characteristic, $event)"
							/>
						</div>
						<div
							v-if="isSelected(characteristic) && !hasChilds(characteristic) && (withExperience || withSelfAssessment)"
							class="characteristic-select-inline--item-additions"
						>
							<div v-if="withExperience">
								<div v-if="experienceConfig && experienceConfig.title" class="font-size-smaller mb-1">
									{{ experienceConfig.title }}
								</div>
								<Select
									:value="getExperience(characteristic)"
									:options="experienceOptions"
									smaller
									@input="setExperience(characteristic, $event)"
								/>
							</div>
							<div v-if="mustChooseSelfAssessment(characteristic)">
								<div class="font-size-smaller my-1">{{ labels.selfAssessmentTitle }}</div>
								<Select
									:value="getSelfAssessment(characteristic)"
									:options="selfAssessmentOptions"
									@input="setSelfAssessment(characteristic, $event)"
								/>
							</div>
							<div v-if="mustChooseRequirement(characteristic)">
								<Dropdown
									:value="getRequirement(characteristic)"
									:custom-options="requirementOptions"
									:placeholder="labels.requirementPlaceholder"
									:multiple="false"
									@input="setRequirement(characteristic, $event)"
								/>
							</div>
						</div>
						<ul
							v-if="showNothingLabel(characteristic)"
							class="characteristic-selected-items level-0"
						>
							<li>
								<a href="#" @click.prevent="openFan(characteristic)">{{ nothingLabelNextLevel }}</a>
							</li>
						</ul>
						<SelectedChilds
							v-if="isSelected(characteristic) && hasSelectedChildsToLastLevel(characteristic)"
							:characteristic="characteristic"
							:selected="selected"
							:no-children-appendix="selectedChildsConfig ? selectedChildsConfig.noChildrenAppendix : undefined"
							@editItem="openFan($event.item, true)"
						/>
						<div
							v-if="characteristic.hint && isSelected(characteristic)"
							v-html="characteristic.hint"
							class="characteristic-select-inline--item-hint"
						/>
					</div>
				</div>
			</div>
		</div>
		<div
			v-if="!shouldShowAll && remainingCount > 0"
			class="characteristic-select-inline--show-more"
		>
			<a
				href="#"
				@click.prevent="showAll = true"
			>
				<Icon icon="caret-down"/>
				{{ remainingCount }} weitere
			</a>
		</div>
		<DeselectPopUp
			v-if="deselectCharacteristic"
			:characteristic="deselectCharacteristic"
			@edit="editAlreadySelectedCharacteristic(deselectCharacteristic)"
			@remove="onDeSelectCharacteristic(deselectCharacteristic)"
			@hidden="dismissCharacteristicRemoval"
		/>
	</div>
</template>

<script>
import _ from "lodash";
import Icon from "@/vue/icon/Icon"
import {isDefined} from "@/helper/Value";
import {arrayIsNotEmpty} from "@/helper/Array";
import {get} from "@/helper/characteristic/Label";
import {getChildIdsToLastLevel, getParent} from "@/helper/application/characteristic/Provider";
import {mapActions, mapState} from "vuex";
import LabelList from "@/vue/characteristic/LabelList";
import {uuid} from "vue-uuid";
import SelectedChilds from "@/vue/characteristic/SelectedChilds";
import {hasSelectedChildsToLastLevel, isSelected} from "@/helper/characteristic/Characteristic";
import {itemsValid} from "@/helper/characteristic/Edit";
import Dropdown from "@/vue/characteristic/Dropdown"
import Supplements from "@/vue/characteristic/Supplements";
import * as LabelSet from "@/helper/characteristic/edit/LabelSet";
import {getOptions} from "@/helper/characteristic/Requirement";
import {isEmptyOrNull} from "@/helper/String";
import {shorten} from "@/helper/characteristic/Shorten";
import SearchInput from "@/vue/common/form/SearchInput.vue";
import Scale from "@/vue/characteristic/Scale.vue";
import ValueLabel from "@/vue/characteristic/select-inline/ValueLabel.vue";
import DeselectPopUp from "@/vue/characteristic/DeselectPopUp.vue";
import Separation from "@/vue/characteristic/separation/Separation.vue";
import {group} from "@/helper/characteristic/GroupByLetter";
import Select from "@/vue/common/form/Select.vue";
import EventData from "@/helper/tracking/EventData";
import * as Event from "@/helper/tracking/Event";
import InfoBox from "@/vue/common/InfoBox.vue";

export default {

	components: {
		InfoBox,
		Select,
		Separation,
		DeselectPopUp,
		ValueLabel,
		Scale,
		SearchInput,
		Supplements,
		SelectedChilds,
		LabelList,
		Icon,
		Dropdown,
	},

	props: {
		value: {
			type: [Object, Array],
			required: false,
		},
		nothingAllowed: {
			type: Object,
			required: false,
		},
		fanConfig: {
			type: Object,
			required: false,
		},
		characteristicItems: {
			type: Array,
			required: true,
		},
		characteristicType: {
			type: Object,
			required: false,
		},
		initialAmount: {
			type: Number,
			required: false,
			default: 5,
		},
		initialShowAll: {
			type: Boolean,
			required: false,
			default: false,
		},
		withKeyword: {
			type: Boolean,
			required: false,
			default: false,
		},
		keywordPlaceholder: {
			type: String,
			required: false,
			default: 'Suche... (mind. 3 Zeichen)'
		},
		withExperience: {
			type: Boolean,
			required: false,
			default: false,
		},
		experienceConfig: {
			type: Object,
			required: false,
		},
		supplementsConfig: {
			type: Object,
			required: false,
		},
		withSelfAssessment: {
			type: Boolean,
			required: false,
			default: false,
		},
		withRequirement: {
			type: Boolean,
			required: false,
			default: false,
		},
		singleChoice: {
			type: Boolean,
			required: false,
			default: false,
		},
		labelType: {
			type: String,
			required: false,
		},
		labelSet: {
			type: String,
			required: false,
		},
		iconPath: {
			type: String,
			required: false,
		},
		postSelectionFunction: {
			type: Function,
			required: false,
		},
		descriptionConfig: {
			type: Object,
			required: false,
		},
		showMultipleHint: {
			type: Boolean,
			required: false,
			default: false,
		},
		groupByLetter: {
			type: Object,
			required: false
		},
		selectedChildsConfig: {
			type: Object,
			required: false
		},
		suppressItemsInfo: {
			type: Boolean,
			required: false,
			default: false,
		},
		disabled: {
			type: Boolean,
			required: false,
			default: false,
		},
		experienceSelectionDropdown: {
			type: Boolean,
			required: false,
			default: false,
		},
		searchCatalog: {
			type: Array,
			required: false,
		}
	},

	data ()
	{
		return {
			key: uuid.v4(),
			selected: this.value,
			showAll: this.initialShowAll,
			deselectCharacteristic: undefined,
			keyword: '',
		};
	},

	computed: {
		...mapState('meta', ['meta']),
		...mapState('characteristic/fan', {
			fanKey: 'key',
			fanShown: 'show'
		}),
		highlightWords ()
		{
			if (!this.hasKeyword)
			{
				return undefined;
			}

			return this.keyword
				.split(' ')
				.map(word => word.trim());
		},
		hasKeyword ()
		{
			return !isEmptyOrNull(this.keyword) && this.keyword.length >= 3;
		},
		shouldShowAll ()
		{
			return this.showAll || this.hasKeyword;
		},
		filteredCharacteristicItems ()
		{
			let items = this.characteristicItems;

			if (this.hasKeyword)
			{
				items = shorten(items, this.keyword);
			}

			if (!this.shouldShowAll)
			{
				items = items.slice(0, this.initialAmount);
			}

			return items;
		},
		itemsGroupedByLetter ()
		{
			if (!this.groupByLetter?.enabled)
			{
				return [{
					id: uuid.v4(),
					items: {
						'': this.filteredCharacteristicItems
					}
				}];
			}

			return group({items: this.filteredCharacteristicItems});
		},
		classes ()
		{
			let classes = [
				'characteristic-select-inline'
			];

			if (this.characteristicType)
			{
				classes.push(
					this.characteristicType.id
				);
			}

			if (this.groupByLetter?.enabled)
			{
				classes.push('characteristic-select-inline--group-by-letter');
			}

			return classes;
		},
		isNothingAllowed ()
		{
			return isDefined(this.nothingAllowed) && this.nothingAllowed.enabled !== false;
		},
		isNothingAllowedByFan ()
		{
			return isDefined(this.fanConfig?.nothingAllowed)
				&& (this.fanConfig?.nothingAllowed?.minimumLevel || 1) <= 1;
		},
		isNothingChecked ()
		{
			return this.isNothingAllowed && isDefined(this.selected) && this.selected.length === 0;
		},
		nothingLabelNextLevel ()
		{
			return this.nothingAllowed?.nextLevelText || 'Alles möglich';
		},
		somethingChosen ()
		{
			return isDefined(this.selected) && arrayIsNotEmpty(this.selected);
		},
		remainingCount ()
		{
			return this.characteristicItems.length - this.initialAmount;
		},
		disableSupplements ()
		{
			return this.supplementsConfig?.disable;
		},
		allSelectionsValid ()
		{
			return itemsValid({
				selected: this.selected,
				withExperience: this.withExperience,
				withSelfAssessment: this.withSelfAssessment,
				withRequirement: this.withRequirement,
				supplementsConfig: this.supplementsConfig
			});
		},
		requirementOptions ()
		{
			return getOptions();
		},
		experienceOptions ()
		{
			return this.getOptionsFromCharacteristicItems(this.meta.workExperience);
		},
		selfAssessmentOptions ()
		{
			return this.getOptionsFromCharacteristicItems(this.meta.skillSelfAssessment);
		},
		labels ()
		{
			return LabelSet.get(this.labelSet || 'application');
		},
		experienceNothingAllowed ()
		{
			if (this.experienceConfig?.nothingAllowed?.enabled === false)
			{
				return undefined;
			}

			return {
				text: this.labels.experienceNone
			};
		}
	},

	methods: {
		...mapActions('characteristic/fan', {
			showFan: 'show',
		}),
		getOptionsFromCharacteristicItems (items)
		{
			return _.map(items, characteristicItem =>
			{
				return {
					value: characteristicItem,
					text: get(characteristicItem, this.labelType)
				};
			});
		},
		showNothingLabel (characteristic)
		{
			return this.isSelected(characteristic)
				&& !this.hasSelectedChildsToLastLevel(characteristic)
				&& this.hasChilds(characteristic)
				&& this.isNothingAllowedByFan;
		},
		hasSelectedChildsToLastLevel (characteristic)
		{
			return hasSelectedChildsToLastLevel(this.selected, characteristic);
		},
		hasChilds (characteristic)
		{
			return characteristic.children.length > 0;
		},
		hasIcon (characteristic)
		{
			return isDefined(this.iconPath) && !isEmptyOrNull(characteristic.naturalId);
		},
		nothingChanged (checked)
		{
			if (checked)
			{
				this.selected = [];
			}
			else
			{
				this.selected = undefined;
			}

			this.emitChangeEvent();
		},
		isSelected (characteristic)
		{
			return isDefined(
				_.find(this.selected, item => item.item.id === characteristic.id)
			);
		},
		onDeSelectCharacteristic (characteristic)
		{
			let action = 'Entfernt';

			if (this.characteristicType)
			{
				action = action + ' (' + this.characteristicType.label + ')';
			}

			Event.push(
				new EventData()
					.setEvent(Event.CHARACTERISTIC_SELECTION)
					.setAction(action)
					.setName(characteristic.label)
			);

			this.removeCharacteristic(characteristic);
		},
		selectOrUnselect (characteristic, checked)
		{
			if (!checked)
			{
				if (this.hasSelectedChildsToLastLevel(characteristic))
				{
					this.deselectCharacteristic = characteristic;

					return;
				}

				this.removeCharacteristic(characteristic);

				let action = 'Entfernt';

				if (this.characteristicType)
				{
					action = action + ' (' + this.characteristicType.label + ')';
				}

				Event.push(
					new EventData()
						.setEvent(Event.CHARACTERISTIC_SELECTION)
						.setAction(action)
						.setName(characteristic.label)
				);

				return;
			}

			let action = 'Hinzugefügt';

			if (this.characteristicType)
			{
				action = action + ' (' + this.characteristicType.label + ')';
			}

			Event.push(
				new EventData()
					.setEvent(Event.CHARACTERISTIC_SELECTION)
					.setAction(action)
					.setName(characteristic.label)
			);

			if (this.singleChoice)
			{
				this.selected = [];
			}

			if (!this.isSelected(characteristic) && !this.hasChilds(characteristic))
			{
				if (!this.selected)
				{
					this.selected = [];
				}

				this.addCharacteristicIfNotYetIn(characteristic);
			}

			if (this.postSelectionFunction)
			{
				if (!this.selected)
				{
					this.selected = [];
				}

				this.selected = this.postSelectionFunction({
					selected: this.selected,
					newlySelectedCharacteristic: characteristic,
				});
			}

			if (this.hasChilds(characteristic))
			{
				this.openFan(characteristic);
			}

			this.emitChangeEvent();
		},
		addCharacteristicIfNotYetIn (characteristic)
		{
			if (!_.find(this.selected, item => item.item.id === characteristic.id))
			{
				this.selected.push({
					item: characteristic
				});
			}
		},
		reSelectCheckbox (characteristic)
		{
			this.setCharacteristicCheckboxChecked(characteristic, true);
		},
		deSelectCheckbox (characteristic)
		{
			this.setCharacteristicCheckboxChecked(characteristic, false);
		},
		setCharacteristicCheckboxChecked (characteristic, checked)
		{
			const refs = this.$refs['characteristic-select-' + characteristic.id];

			if (!refs)
			{
				return;
			}

			const ref = refs[0];

			if (!ref)
			{
				return;
			}

			ref
				.$el
				.getElementsByTagName('input')[0]
				.checked = checked;
		},
		dismissCharacteristicRemoval ()
		{
			this.reSelectCheckbox(this.deselectCharacteristic);

			this.deselectCharacteristic = undefined;
		},
		editAlreadySelectedCharacteristic (characteristic)
		{
			this.reSelectCheckbox(characteristic);

			this.openFan(characteristic);

			this.deselectCharacteristic = undefined;
		},
		removeCharacteristic (characteristic)
		{
			this.selected = _.filter(this.selected, item => item.item.id !== characteristic.id);

			const childIds = getChildIdsToLastLevel(characteristic, this.selected);

			this.selected = _.filter(this.selected, item => !_.includes(childIds, item.item.id));

			if (this.postSelectionFunction)
			{
				this.selected = this.postSelectionFunction({
					selected: this.selected,
					removedCharacteristic: characteristic,
				});
			}

			this.deselectCharacteristic = undefined;

			this.emitChangeEvent();
		},
		openFan (characteristic, reload)
		{
			if (reload)
			{
				characteristic = getParent(characteristic.id, this.characteristicItems);
			}

			this.showFan({
				key: this.key,
				selected: this.selected,
				characteristicType: this.characteristicType,
				characteristicItems: characteristic.children,
				characteristicMetaProperty: this.fanConfig?.characteristicMetaProperty,
				title: get(characteristic),
				titleFormats: this.fanConfig?.titleFormats,
				info: this.fanConfig?.info,
				withKeyword: this.fanConfig?.withKeyword,
				searchConfig: this.fanConfig?.searchConfig,
				nothingAllowed: this.fanConfig?.nothingAllowed,
				withExperience: this.withExperience,
				withSelfAssessment: this.withSelfAssessment,
				supplementsConfig: this.supplementsConfig,
				sourceCharacteristic: characteristic,
				groupByLetter: this.fanConfig?.groupByLetter,
				labelType: this.labelType,
				showMultipleHint: this.fanConfig?.showMultipleHint || false,
				maxLevel: this.fanConfig?.maxLevel,
				onChange: ({selected}) =>
				{
					this.selected = selected;

					if (isSelected(selected, characteristic) || hasSelectedChildsToLastLevel(selected, characteristic))
					{
						this.addCharacteristicIfNotYetIn(characteristic);
					}
					else
					{
						this.deSelectCheckbox(characteristic);
					}

					this.emitChangeEvent();
				},
				onClose: () =>
				{
					if (!this.isSelected(characteristic))
					{
						this.deSelectCheckbox(characteristic);
					}
				}
			});
		},
		relevantItemCharacteristicTypes (characteristic)
		{
			const itemCharacteristicTypes = characteristic.itemCharacteristicTypes;

			if (!this.supplementsConfig?.types)
			{
				return itemCharacteristicTypes;
			}

			return _.filter(
				itemCharacteristicTypes,
				item =>
				{
					return _.includes(this.supplementsConfig.types, item.type.id);
				}
			);
		},
		hasItemCharacteristicTypes (characteristic)
		{
			return this.relevantItemCharacteristicTypes(characteristic).length > 0;
		},
		mustChooseSupplements (characteristic)
		{
			if (!this.isSelected(characteristic) || !this.hasItemCharacteristicTypes(characteristic) || this.disableSupplements)
			{
				return false;
			}

			return !this.hasChilds(characteristic);
		},
		getInitialSupplements (characteristic, itemCharacteristicType)
		{
			const selectedItem = _.find(this.selected, item => item.item.id === characteristic.id);

			if (!selectedItem.supplements)
			{
				return undefined;
			}

			const item = _.find(
				selectedItem.supplements,
				item => item.characteristicType.id === itemCharacteristicType.type.id
			);

			return item?.items;
		},
		setSupplements (characteristic, data)
		{
			const item = _.find(this.selected, item => item.item.id === characteristic.id);

			const characteristicType = data.itemCharacteristicType.type;

			let supplements = item.supplements;

			if (!supplements)
			{
				supplements = [];
			}

			supplements = _.filter(
				supplements,
				s => s.characteristicType.id !== characteristicType.id
			);

			supplements.push({
				characteristicType,
				items: data.items,
			});

			this.$set(item, 'supplements', supplements);

			this.emitChangeEvent();
		},
		getExperience (characteristic)
		{
			const item = _.find(this.selected, item => item.item.id === characteristic.id);

			return item?.experience;
		},
		setExperience (characteristic, experience)
		{
			let action = 'Erfahrung';

			if (this.characteristicType)
			{
				action = action + ' (' + this.characteristicType.label + ')';
			}

			Event.push(
				new EventData()
					.setEvent(Event.CHARACTERISTIC_SELECTION)
					.setAction(action)
					.setName(characteristic.label + ': ' + experience.label)
			);

			const item = _.find(this.selected, item => item.item.id === characteristic.id);

			this.$set(item, 'experience', experience);

			if (!experience)
			{
				this.setSelfAssessment(characteristic, undefined);
			}

			this.emitChangeEvent();
		},
		mustChooseSelfAssessment (characteristic)
		{
			if (!this.withSelfAssessment)
			{
				return false;
			}

			if (this.withExperience)
			{
				return isDefined(
					this.getExperience(characteristic)
				)
			}

			return true;
		},
		mustChooseRequirement (characteristic)
		{
			if (!this.withRequirement)
			{
				return false;
			}

			if (this.withExperience)
			{
				return isDefined(
					this.getExperience(characteristic)
				)
			}

			return true;
		},
		getSelfAssessment (characteristic)
		{
			const item = _.find(this.selected, item => item.item.id === characteristic.id);

			return item?.selfAssessment;
		},
		setSelfAssessment (characteristic, selfAssessment)
		{
			Event.push(
				new EventData()
					.setEvent(Event.CHARACTERISTIC_SELECTION)
					.setAction('Selbsteinschätzung')
					.setName(characteristic.label + ': ' + selfAssessment.label)
			);

			const item = _.find(this.selected, item => item.item.id === characteristic.id);

			this.$set(item, 'selfAssessment', selfAssessment);

			this.emitChangeEvent();
		},
		getRequirement (characteristic)
		{
			const item = _.find(this.selected, item => item.item.id === characteristic.id);

			return item?.requirement;
		},
		setRequirement (characteristic, requirement)
		{
			const item = _.find(this.selected, item => item.item.id === characteristic.id);

			this.$set(item, 'requirement', requirement);

			this.emitChangeEvent();
		},
		emitChangeEvent ()
		{
			if (!this.allSelectionsValid)
			{
				this.$emit('input', undefined);
				return;
			}

			if (!this.isNothingAllowed && !this.isNothingChecked && !this.somethingChosen)
			{
				this.$emit('input', undefined);
				return;
			}

			this.$emit('validChange', this.selected);
			this.$emit('input', this.selected);
		}
	},

	watch: {
		value (v)
		{
			this.value = v;
		},
		fanShown (show)
		{
			if (show)
			{
				return;
			}

			const selectedIds = _.map(this.selected, 'item.id');

			// uncheck all checkboxes on which do not have selected childs
			_.forEach(this.characteristicItems, characteristic =>
			{
				if (characteristic.children.length === 0)
				{
					return true; // continue
				}

				const childIds = getChildIdsToLastLevel(characteristic);
				const selfSelected = !_.includes(selectedIds, characteristic.id);

				// no childs selected and not self selected (via nothing)
				if (
					_.intersection(selectedIds, childIds).length === 0
					&& !selfSelected
					&& !this.isNothingAllowedByFan
				)
				{
					this.selected = _.filter(this.selected, item => item.item.id !== characteristic.id);
				}
			});

			this.emitChangeEvent();
		}
	}
}
</script>
